import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleDot, Loader2, Sparkles } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { usePermissions } from '../../hooks/usePermissions';
import { Action, Chip, ErrorNote, Kicker, ListRow, Panel, RouteMark } from '../../components/aa';
import {
  recommendRoute, ROUTE_LABEL, ROUTE_MEANING, DELIVERABLE_LABEL, DELIVERABLE_MEANING,
  type ActionKind, type Audience, type Frequency, type Impact, type InputKind, type RoutingAnswers,
} from './routeRequest';

/*
 * Ask Orbit: the full-screen request flow.
 *
 * Three steps, and the person can see which one they are on the whole way:
 * say what you want, answer the few things that actually change the route,
 * then read the recommendation and the reasons behind it before anything is
 * created.
 *
 * It takes the whole screen rather than a side drawer on purpose. This is
 * intake, not chat: the answers here decide the route, the owner, the
 * controls, and the commercial treatment, and a 380px drawer over a dashboard
 * says "quick question" about a decision that is none of those things.
 *
 * The original wording stays the requester's. It is sent as the request detail
 * and never rewritten on the way.
 */

const STEPS = 3;

interface Choice<T> {
  value: T;
  label: string;
}

const AUDIENCE: Choice<Audience>[] = [
  { value: 'me', label: 'Only me' },
  { value: 'team', label: 'My team' },
  { value: 'teams', label: 'Several teams' },
  { value: 'company', label: 'The whole company' },
];

const FREQUENCY: Choice<Frequency>[] = [
  { value: 'once', label: 'Once' },
  { value: 'repeated', label: 'Repeatedly' },
  { value: 'scheduled', label: 'On a schedule' },
  { value: 'continuous', label: 'Continuously' },
];

const INPUT: Choice<InputKind>[] = [
  { value: 'personal', label: 'My own files' },
  { value: 'context', label: 'Company Context' },
  { value: 'app', label: 'A connected app' },
  { value: 'restricted', label: 'Restricted data' },
];

const ACTION: Choice<ActionKind>[] = [
  { value: 'read', label: 'Read and summarise' },
  { value: 'draft', label: 'Draft something' },
  { value: 'recommend', label: 'Recommend a decision' },
  { value: 'act', label: 'Act inside an app' },
];

const IMPACT: Choice<Impact>[] = [
  { value: 'easy', label: 'Easy to correct' },
  { value: 'costly', label: 'Costly to correct' },
  { value: 'material', label: 'Material' },
  { value: 'regulated', label: 'Regulated' },
];

const PROMPTS = [
  'Prepare my weekly update',
  'Find the right policy',
  'Coordinate team approvals',
  'Review supplier payments safely',
];

const ChoiceRow = <T extends string>({ label, hint, options, value, onChange }: {
  label: string;
  hint: string;
  options: Choice<T>[];
  value: T;
  onChange: (next: T) => void;
}) => (
  <div className="border-b border-line-soft py-5 last:border-b-0">
    <div className="text-[14px] font-semibold text-ink">{label}</div>
    <div className="mt-1 text-[13px] text-ink-muted">{hint}</div>
    <div className="mt-3 flex flex-wrap gap-2">
      {options.map((o) => {
        const selected = o.value === value;
        const tone = {
          true: 'border-ink bg-ink font-medium text-white',
          false: 'border-input bg-white text-[#303238] hover:bg-canvas-inset',
        }[String(selected)];
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(o.value)}
            className={`rounded-full border px-2.5 py-2 text-[12px] transition-colors ${tone}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const Spinner: React.FC<{ busy: boolean }> = ({ busy }) => {
  if (!busy) return null;
  return <Loader2 size={14} className="animate-spin" />;
};

const OrbitRequest: React.FC = () => {
  const navigate = useNavigate();
  // Whoever holds the final gate settles their own request as they write it.
  // The server does the settling; this only decides what the screen promises.
  const { can } = usePermissions();
  const settlesOwn = can('aai_approve_requests');
  const [step, setStep] = useState(1);
  const [description, setDescription] = useState('');
  const [answers, setAnswers] = useState<RoutingAnswers>({
    audience: 'me',
    frequency: 'once',
    input: 'personal',
    action: 'read',
    impact: 'easy',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recommendation = useMemo(() => recommendRoute(answers), [answers]);
  const described = description.trim().length > 0;

  // The request title is the first line of what they wrote, so the record they
  // find later is named in their own words rather than in ours.
  const title = useMemo(() => {
    const firstLine = description.trim().split('\n')[0] || '';
    if (firstLine.length <= 120) return firstLine;
    return `${firstLine.slice(0, 117)}...`;
  }, [description]);

  let settleNote = 'Sending this creates a record your department head reviews. Your original wording goes with it and stays yours. Nothing runs and nothing is shared until it is approved.';
  let stateLabel = 'Private until approved';
  let footLabel = 'Private request';
  let submitLabel = 'Send request';
  if (settlesOwn) {
    settleNote = 'You hold the final approval, so this is settled the moment you finish it. Your original wording goes with it and stays yours, and it opens in Projects.';
    stateLabel = 'Yours to settle';
    footLabel = 'Your request';
    submitLabel = 'Finish and open it';
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await aaiApi.createRequest({
        title,
        detail: description.trim(),
        risk: recommendation.risk,
        // The route is its own field. It used to be sent as the capability tag,
        // which holds a fixed set of five capabilities and has never included a
        // route, so the body model rejected every submission from this flow.
        route: recommendation.route,
        deliverable: recommendation.deliverable,
        // The sentence behind the shape travels with it. Without it the Owner
        // approving the request sees the pill and nothing else, while the
        // person raising it read the reason on this very screen.
        deliverable_reason: recommendation.deliverableReason,
        capability_tag: recommendation.capability,
      });
      if (settlesOwn) {
        navigate('/projects');
        return;
      }
      navigate('/my-projects');
    } catch {
      setError('Could not save your request. Nothing was created, so you can try again.');
      setBusy(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="shrink-0">
        <div className="flex h-16 items-center gap-4 px-5 sm:px-[30px]">
          <span className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-tile bg-accent-500 text-ink">
              <Sparkles size={18} strokeWidth={2} />
            </span>
            <span>
              <span className="block text-[13px] font-bold leading-tight text-ink">Orbit</span>
              <span className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-soft">
                Private request
              </span>
            </span>
          </span>
          <span className="flex-1 text-center text-[13px] text-ink-muted">{step} of {STEPS}</span>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="text-[13px] text-ink-muted transition-colors hover:text-ink"
          >
            Pick up later
          </button>
        </div>
        <div className="flex gap-0.5" aria-hidden>
          {[1, 2, 3].map((n) => {
            const fill = { true: 'bg-accent-500', false: 'bg-line' }[String(n <= step)];
            return <span key={n} className={`h-[3px] flex-1 ${fill}`} />;
          })}
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center px-5 py-10 sm:px-[30px] sm:py-14">
          <div className="w-full max-w-[720px]">
          {error && <ErrorNote className="mb-6" message={error} />}

          {step === 1 && (
            <>
              <p className="m-0 mb-[7px] text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                New request
              </p>
              <h1 className="m-0 text-[30px] font-bold leading-[35px] tracking-[-0.02em] text-ink sm:text-[40px] sm:leading-[46px]">
                What would you like to make easier?
              </h1>
              <p className="mb-0 mt-3 text-[14px] leading-[23px] text-ink-muted">
                Describe the result you need. Orbit will recommend a Quick Win or Project and show why.
              </p>
              <textarea
                autoFocus
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Checking technical proposals against our standards takes too long..."
                className="mt-6 min-h-[136px] w-full resize-y rounded-act border border-input bg-white p-4 text-[15px] leading-[26px] text-ink outline-none transition-colors placeholder:text-ink-soft focus:border-ink"
              />
              <div className="mt-4 flex flex-wrap gap-2.5">
                {PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setDescription(p)}
                    className="rounded-full border border-input bg-white px-2.5 py-2 text-[12px] text-[#303238] transition-colors hover:bg-canvas-inset"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="m-0 mb-[7px] text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                What changes the route
              </p>
              <h1 className="m-0 text-[30px] font-bold leading-[35px] tracking-[-0.02em] text-ink sm:text-[40px] sm:leading-[46px]">
                A few things that decide how this is delivered.
              </h1>
              <p className="mb-0 mt-3 text-[14px] leading-[23px] text-ink-muted">
                Risk and size are separate questions. These five answers set the route, the owner, and the controls.
              </p>
              <div className="mt-6">
                <ChoiceRow label="Who is it for?" hint="Reach decides whether one owner is enough."
                  options={AUDIENCE} value={answers.audience}
                  onChange={(audience) => setAnswers({ ...answers, audience })} />
                <ChoiceRow label="How often?" hint="Anything beyond once needs a version and a review date."
                  options={FREQUENCY} value={answers.frequency}
                  onChange={(frequency) => setAnswers({ ...answers, frequency })} />
                <ChoiceRow label="What does it read?" hint="Source rights are checked separately from your role."
                  options={INPUT} value={answers.input}
                  onChange={(input) => setAnswers({ ...answers, input })} />
                <ChoiceRow label="What does it do?" hint="Reading is not the same boundary as acting."
                  options={ACTION} value={answers.action}
                  onChange={(action) => setAnswers({ ...answers, action })} />
                <ChoiceRow label="If it gets it wrong?" hint="How hard the mistake is to correct, not how likely it is."
                  options={IMPACT} value={answers.impact}
                  onChange={(impact) => setAnswers({ ...answers, impact })} />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <p className="m-0 mb-[7px] text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                Recommended route
              </p>
              <h1 className="m-0 text-[30px] font-bold leading-[35px] tracking-[-0.02em] text-ink sm:text-[40px] sm:leading-[46px]">
                {ROUTE_LABEL[recommendation.route]}, built as a {DELIVERABLE_LABEL[recommendation.deliverable].toLowerCase()}
              </h1>
              <p className="mb-0 mt-3 text-[14px] leading-[23px] text-ink-muted">
                {ROUTE_MEANING[recommendation.route]}
              </p>
              <p className="mb-0 mt-2 text-[14px] leading-[23px] text-ink-muted">
                {DELIVERABLE_MEANING[recommendation.deliverable]}
              </p>

              <Panel className="mt-6">
                <div className="px-6 pb-2 pt-6">
                  <Kicker>Why this route and this shape</Kicker>
                </div>
                <ul className="m-0 list-none px-6 pb-5 pt-1">
                  {[...recommendation.reasons, recommendation.deliverableReason].map((r) => (
                    <li key={r} className="flex gap-2.5 py-1.5 text-[13px] leading-[21.5px] text-ink">
                      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-ink-soft" />
                      {r}
                    </li>
                  ))}
                </ul>
              </Panel>

              {recommendation.controls.length > 0 && (
                <Panel className="mt-5">
                  <div className="px-6 pb-2 pt-6">
                    <Kicker>Controls that apply</Kicker>
                  </div>
                  <ul className="m-0 list-none px-6 pb-5 pt-1">
                    {recommendation.controls.map((c) => (
                      <li key={c} className="flex gap-2.5 py-1.5 text-[13px] leading-[21.5px] text-ink-muted">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-ink-soft" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}

              <Panel className="mt-5">
                <ListRow
                  mark={<RouteMark kind={recommendation.route} size="lg" />}
                  title={title || 'Your request'}
                  desc={description.trim()}
                  right={<Chip tone="private">{stateLabel}</Chip>}
                />
              </Panel>

              <p className="mt-5 text-[13px] leading-[21.5px] text-ink-muted">
                {settleNote}
              </p>
            </>
            )}
          </div>
        </div>
      </main>

      <footer className="shrink-0 border-t border-line bg-white">
        <div className="mx-auto flex w-full max-w-[720px] items-center justify-between gap-4 px-5 py-4 sm:px-[30px] lg:px-0">
          <span className="inline-flex items-center gap-2 text-[13px] text-ink-muted">
            <CircleDot size={14} /> {footLabel}
          </span>
          <span className="flex items-center gap-2">
            {step > 1 && (
              <Action variant="outline" onClick={() => setStep(step - 1)}>Back</Action>
            )}
            {step < STEPS && (
              <Action
                arrow
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !described}
                reason="Describe the result you need first."
              >
                Continue
              </Action>
            )}
            {step === STEPS && (
              <Action onClick={submit} disabled={busy} reason="Saving your request." icon={<Spinner busy={busy} />}>
                {submitLabel}
              </Action>
            )}
          </span>
        </div>
      </footer>
    </div>
  );
};

export default OrbitRequest;
